import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const RISK_03: GgxTourDefinition = {
    key: '[RISK-03] Identifying Risks of a GenAI Application',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 5,
    prerequisite: {
        target: '[test-id="search-button"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'Which risks actually matter for your application?',
        subtitle:
            'The full list of GenAI risks is long, and no team can test for all of it with ' +
            'equal care. The first real step in governing an application is deciding which ' +
            'risks matter most for this use case, because that decision shapes every ' +
            'evaluation and guardrail that follows. This tour walks through how to make that ' +
            'call, using the credit card chatbot as the worked example.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You have seen how to go from the long list of GenAI risks to the short list that ' +
            'matters for one application: weigh each risk by its financial impact, its ' +
            'regulatory exposure, how much a human will review the output, and what data the ' +
            'system can reach. You also saw how the design of the response step changes the ' +
            'answer, and where that short list is recorded in GGX. Next, Running your first ' +
            'evaluation shows how to measure the risks you picked with a report, so the weak ' +
            'spots show up as numbers rather than opinions.',
        learned: [
            'Weigh each risk by **financial impact and regulatory exposure**.',
            '**Human review and data reach** change the weight too.',
            'The **short list is recorded** on the pipeline.',
        ],
    },
    chapters: [
        {
            name: 'Find the pipeline',
            blurb: 'Search from anywhere and jump straight to it',
            icon: 'search',
            steps: [2, 3, 4],
        },
        {
            name: 'Pick the risks that matter',
            blurb: 'A video, a way to weigh them, and a worked example',
            icon: 'cap',
            steps: [5, 6, 7],
        },
        {
            name: 'Where it is recorded',
            blurb: 'The short list lives on the pipeline',
            icon: 'map',
            steps: [8],
        },
    ],
    steps: [
        {
            target: '[test-id="search-button"]',
            title: 'Start from the search box',
            whatHappens: 'a search box opens in the middle of the screen.',
            icon: 'search',
            description:
                'We will end on a real pipeline, so let us find it first. The global search ' +
                'finds any object by name from any page, and <strong>Ctrl+K</strong> opens it ' +
                'too. Click <strong>Search</strong>.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Search',
        },
        {
            target: '[test-id="global-search"] input',
            title: 'Type the name of the chatbot',
            whatHappens:
                'matching objects appear below the box as you type, and the tour moves on when ' +
                'the pipeline is among them.',
            icon: 'search',
            description:
                'Click the tag to copy the name, paste it into the search box, and watch the ' +
                'results narrow down.<br><br>' +
                '<code data-ggx-copy>Combined Customer Intent and Response Pipeline</code>',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetMount: true,
            waitCue: 'Type the name to continue',
        },
        {
            target: '[test-id^="global-search-option-"]',
            targetText: 'Combined Customer Intent and Response Pipeline v1',
            scrollIntoView: true,
            title: 'Pick the pipeline itself',
            whatHappens: "the pipeline's page opens on its Details tab.",
            icon: 'search',
            description:
                'A few objects share this name. The one we want is the pipeline marked ' +
                '<strong>v1</strong>. Click <strong>Combined Customer Intent and Response ' +
                'Pipeline</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Combined Customer Intent and Response Pipeline',
        },
        {
            title: 'Identifying risks in GenAI',
            whatHappens: 'next, a simple way to weigh one risk against another.',
            icon: 'cap',
            description:
                'This short video shows how to get from the broad risks of an AI application ' +
                '(model, financial, reputational, data, operational, legal and more) to the ' +
                'specific performance and risk attributes you can actually test. Watch it, ' +
                'then click Continue.',
            benefits: [
                'Accuracy',
                'Stability',
                'Reliability',
                'Fairness',
                'Ethics',
                'Vulnerability',
                'Transparency',
                'Governance',
            ],
            video: {
                src: 'https://genguardx.ai/tours/risk-03-identifying-risks.mp4',
                title: 'Identifying Risks in GenAI',
            },
        },
        {
            title: 'How much does a risk matter here?',
            whatHappens: 'next, the short list for our chatbot.',
            icon: 'cap',
            description:
                'The same risk can be minor in one application and critical in another. Four ' +
                'questions do most of the work in deciding which is which:' +
                '<ul data-ggx-list>' +
                '<li>Could the risk cause a <strong>large financial impact</strong>?</li>' +
                '<li>Could it <strong>break a regulation or a business policy</strong>?</li>' +
                '<li>Will a <strong>human review the output</strong> before it reaches ' +
                'anyone, and how thoroughly?</li>' +
                '<li>Does the system have access to <strong>sensitive data</strong>, or can ' +
                'it <strong>change data</strong>?</li>' +
                '</ul>' +
                '<aside data-ggx-use="For our chatbot">It quotes card balances to customers ' +
                'with no human in between, so it scores high on three of the four. Accuracy and ' +
                'vulnerability go to the top of its list.</aside>' +
                'Click Continue.',
        },
        {
            title: 'The short list for the card response model',
            whatHappens: 'next, where this list is recorded in GGX.',
            icon: 'cap',
            description:
                'The diagram works through our chatbot. Intent classification comes first, ' +
                'then the card-related response, and how that response is built changes the ' +
                'risks. Open each tab, then click Continue.',
            tabs: [
                {
                    label: 'Situation A: generative',
                    html:
                        '<p data-ggx-lead>The reply is written freely by the model from the ' +
                        'retrieved card data.</p>' +
                        '<ul data-ggx-list>' +
                        '<li><strong>High priority</strong>: accuracy, hallucination and ' +
                        'fabrication, jailbreak, business policy adherence.</li>' +
                        '<li><strong>Still there</strong>: implicit bias, latency and cost, ' +
                        'output variability.</li>' +
                        '</ul>' +
                        '<aside data-ggx-use="In practice">The most natural conversation, and ' +
                        'the longest list of things to measure.</aside>',
                },
                {
                    label: 'Situation B: templated',
                    html:
                        '<p data-ggx-lead>The model only picks and fills approved templated ' +
                        'phrases.</p>' +
                        '<ul data-ggx-list>' +
                        '<li><strong>Fades</strong>: hallucination, jailbreak and policy ' +
                        'adherence, because the model cannot say anything off the list.</li>' +
                        '<li><strong>Stays high</strong>: implicit bias, because the data it ' +
                        'fills in can still treat customers unevenly.</li>' +
                        '</ul>' +
                        '<aside data-ggx-use="In practice">Same use case, different design, ' +
                        'different short list. Mitigating Risks through Constraining LLMs shows ' +
                        'how this version is built.</aside>',
                },
            ],
            image: {
                src: 'https://genguardx.ai/tours/risk-03-key-risks.png',
                title: 'Key risks for the card response model',
                caption:
                    'Situation A, a fully generative response, against Situation B, templated ' +
                    'responses selected by the LLM.',
            },
        },
        {
            target: '[test-id="tab-item-Risk Assessment"]',
            scrollIntoView: true,
            title: 'Where the short list lives',
            whatHappens: 'the Risk Assessment tab opens and the tour wraps up.',
            icon: 'map',
            description:
                'Once you have decided which risks matter, the <strong>Risk Assessment</strong> ' +
                'tab is where they are recorded for this pipeline, category by category, with ' +
                'how each was measured and mitigated. Your organisation can shape the ' +
                'categories and attributes to its own taxonomy. Click <strong>Risk ' +
                'Assessment</strong> to open it and finish.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Risk Assessment',
        },
    ],
};
